import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import CONFIG from "../config";
import { usePatient } from "../context/PatientContext";
import { useTheme } from "../context/ThemeContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  Search,
  PlusCircle,
  Building2,
  UserPlus,
  Loader2,
  UserSquare2,
  ChevronRight,
  Stethoscope,
  Power,
  ShieldCheck,
  ShieldAlert
} from "lucide-react";

const ListofDoctors = () => {
  const [doctors, setDoctors] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [clinics, setClinics] = useState({});
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const baseURL = CONFIG.BASE_URL;
  const token = localStorage.getItem("authToken");
  const { doctorInfo } = usePatient();
  const { theme, isDarkMode } = useTheme();

  useEffect(() => {
    setLoading(true);
    const headers = {
      Authorization: `Bearer ${token}`,
      doctorId: doctorInfo.MANDALI_ID,
      clinicId: doctorInfo.CLINIC_ID,
      roleId: doctorInfo.MANDALI_ROLE
    };

    axios.get(`${baseURL}/doctors/doctorclinics-list`, { headers })
      .then((res) => {
        // Since a doctor might appear multiple times if they belong to multiple clinics,
        // we can group them or just list them as is. 
        // Based on the previous UI, it seems it expected one row per doctor.
        // Let's group clinics by doctor ID.
        const grouped = res.data.reduce((acc, curr) => {
          if (!acc[curr.MANDALI_ID]) {
            acc[curr.MANDALI_ID] = {
              ...curr,
              clinics: curr.CLINIC_NAME ? [curr.CLINIC_NAME] : []
            };
          } else if (curr.CLINIC_NAME) {
            acc[curr.MANDALI_ID].clinics.push(curr.CLINIC_NAME);
          }
          return acc;
        }, {});

        setDoctors(Object.values(grouped));
        setLoading(false);
      })
      .catch(err => {
        console.error("Error fetching doctor clinics:", err);
        setLoading(false);
      });
  }, [baseURL, token, doctorInfo]);

  const handleToggleStatus = (doctor) => {
    const newStatus = !doctor.IS_ACTIVE;
    const headers = {
      Authorization: `Bearer ${token}`,
      doctorId: doctorInfo.MANDALI_ID,
      clinicId: doctorInfo.CLINIC_ID,
      roleId: doctorInfo.MANDALI_ROLE
    };

    axios.put(`${baseURL}/doctors/toggle-status/${doctor.MANDALI_ID}`, { isActive: newStatus }, { headers })
      .then(() => {
        setDoctors(prev => prev.map(d =>
          d.MANDALI_ID === doctor.MANDALI_ID ? { ...d, IS_ACTIVE: newStatus } : d
        ));
      })
      .catch(err => console.error("Error toggling status:", err));
  };

  const getRoleName = (roleId) => {
    switch (parseInt(roleId)) {
      case 1: return "Admin";
      case 2: return "Doctor";
      case 3: return "Attendant";
      default: return "Staff";
    }
  };

  const filteredDoctors = doctors.filter((doc) =>
    doc.MANDALI_FULL_NAME?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.clinics.some(c => c.toLowerCase().includes(searchTerm.toLowerCase())) ||
    getRoleName(doc.MANDALI_ROLE).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const tableHeaderStyle = {
    backgroundColor: isDarkMode ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
    color: theme.text,
    borderColor: theme.border,
    fontWeight: "700",
    textTransform: "uppercase",
    fontSize: "11px",
    letterSpacing: "0.15em",
    padding: "1.25rem 2rem",
    borderBottom: `2px solid ${theme.border}`,
    textAlign: "left"
  };

  const tableCellStyle = {
    color: theme.text,
    borderColor: theme.border,
    fontSize: "15px",
    padding: "1.25rem 2rem",
    borderBottom: `1px solid ${theme.border}`
  };

  return (
    <div className="min-h-screen p-4 md:p-8 transition-colors duration-500">
      <div className="max-w-[1400px] mx-auto">

        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10 flex flex-col md:flex-row justify-between items-center gap-6"
        >
          <div>
            <h2 className="text-3xl md:text-4xl font-serif mb-2" style={{ color: theme.text, fontFamily: theme.fonts?.serif }}>
              Medical Professionals
            </h2>
            <p className="text-lg opacity-80" style={{ color: theme.textSecondary }}>
              Management of active doctors and associated clinics
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <ActionButton
              onClick={() => navigate("/add-clinic")}
              icon={Building2}
              label="Add Clinic"
              theme={theme}
              isDarkMode={isDarkMode}
            />
            <ActionButton
              onClick={() => navigate("/add-doctor")}
              icon={UserPlus}
              label="Add Doctor"
              theme={theme}
              isDarkMode={isDarkMode}
              primary
            />
            <ActionButton
              onClick={() => navigate("/add-attendant")}
              icon={PlusCircle}
              label="Add Attendant"
              theme={theme}
              isDarkMode={isDarkMode}
            />
          </div>
        </motion.div>

        {/* Search & Stats Section */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 mb-8">
          <div className="lg:col-span-3">
            <div className="relative">
              <Search className="absolute left-6 top-1/2 -translate-y-1/2 opacity-40" size={20} style={{ color: theme.text }} />
              <input
                type="text"
                placeholder="Search by Doctor or Clinic Name..."
                className="w-full pl-14 pr-6 py-4 rounded-3xl border outline-none focus:ring-2 transition-all shadow-xl"
                style={{
                  backgroundColor: theme.inputBg,
                  borderColor: theme.border,
                  color: theme.text,
                  '--tw-ring-color': theme.primary + '30'
                }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="lg:col-span-1">
            <div className="h-full flex items-center justify-between px-6 py-4 border rounded-3xl shadow-xl shadow-black/5" style={{ backgroundColor: theme.inputBg, borderColor: theme.border }}>
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl" style={{ backgroundColor: theme.primary + '20', color: theme.primary }}>
                  <Users size={20} />
                </div>
                <span className="font-bold opacity-60 uppercase text-[10px] tracking-widest" style={{ color: theme.textSecondary }}>Total Staff</span>
              </div>
              <span className="text-2xl font-bold" style={{ color: theme.text }}>{doctors.length}</span>
            </div>
          </div>
        </div>

        {/* Table Section */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          className="rounded-[2.5rem] overflow-hidden shadow-[0_50px_100px_-20px_rgba(0,0,0,0.3)] border"
          style={{ backgroundColor: theme.card, borderColor: theme.border }}
        >
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" style={{ backgroundColor: theme.inputBg }}>
              <thead>
                <tr style={{ backgroundColor: isDarkMode ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.03)' }}>
                  <th style={tableHeaderStyle}>Staff Member</th>
                  <th style={tableHeaderStyle}>Role</th>
                  <th style={tableHeaderStyle}>Associated Clinic</th>
                  <th style={tableHeaderStyle}>Status</th>
                  <th style={{ ...tableHeaderStyle, textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: theme.border }}>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="py-24 text-center">
                      <Loader2 className="animate-spin mx-auto mb-4" size={48} style={{ color: theme.primary }} />
                      <p className="font-medium opacity-50" style={{ color: theme.text }}>Fetching staff records...</p>
                    </td>
                  </tr>
                ) : filteredDoctors.length > 0 ? (
                  filteredDoctors.map((doctor, idx) => (
                    <motion.tr
                      key={doctor.MANDALI_ID}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.03 }}
                      className="group transition-colors"
                      style={{
                        borderBottom: `1px solid ${theme.border}`,
                        backgroundColor: isDarkMode ? theme.inputBg : 'inherit',
                        opacity: doctor.IS_ACTIVE ? 1 : 0.6,
                        filter: doctor.IS_ACTIVE ? 'none' : 'grayscale(0.4)'
                      }}
                      whileHover={{
                        backgroundColor: isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)"
                      }}
                    >
                      <td style={tableCellStyle}>
                        <div className="flex items-center gap-4">
                          <div className="w-11 h-11 rounded-2xl flex items-center justify-center font-bold text-lg border shadow-sm"
                            style={{ backgroundColor: theme.primary + '15', color: theme.primary, borderColor: theme.primary + '20' }}>
                            {doctor.MANDALI_FULL_NAME?.charAt(0) || "S"}
                          </div>
                          <div>
                            <p className="font-bold text-lg" style={{ color: theme.text }}>{doctor.MANDALI_FULL_NAME}</p>
                            <p className="text-xs font-semibold opacity-60 uppercase tracking-wider" style={{ color: theme.textSecondary }}>ID: #{doctor.MANDALI_ID}</p>
                          </div>
                        </div>
                      </td>
                      <td style={tableCellStyle}>
                        <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider shadow-sm"
                          style={{
                            backgroundColor: doctor.MANDALI_ROLE === 1 ? '#EF444420' : (doctor.MANDALI_ROLE === 2 ? '#3B82F620' : '#10B98120'),
                            color: doctor.MANDALI_ROLE === 1 ? '#EF4444' : (doctor.MANDALI_ROLE === 2 ? '#3B82F6' : '#10B981'),
                            border: `1px solid ${doctor.MANDALI_ROLE === 1 ? '#EF444440' : (doctor.MANDALI_ROLE === 2 ? '#3B82F640' : '#10B98140')}`
                          }}
                        >
                          {getRoleName(doctor.MANDALI_ROLE)}
                        </div>
                      </td>
                      <td style={tableCellStyle}>
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg" style={{ backgroundColor: theme.primary + '10' }}>
                            <Building2 size={16} style={{ color: theme.primary }} className="opacity-70" />
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {doctor.clinics.length > 0 ? (
                              doctor.clinics.map((clinic, i) => (
                                <span key={i} className="font-semibold text-base" style={{ color: theme.text }}>
                                  {clinic}{i < doctor.clinics.length - 1 ? ", " : ""}
                                </span>
                              ))
                            ) : (
                              <span className="opacity-40 italic" style={{ color: theme.text }}>No Clinic Assigned</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={tableCellStyle}>
                        <button
                          onClick={() => handleToggleStatus(doctor)}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-xl font-bold text-[10px] uppercase tracking-wider transition-all border ${doctor.IS_ACTIVE
                            ? "bg-green-500/10 text-green-500 border-green-500/20 hover:bg-green-500/20"
                            : "bg-gray-500/10 text-gray-400 border-gray-500/20 hover:bg-gray-500/20"
                            }`}
                        >
                          {doctor.IS_ACTIVE ? (
                            <>
                              <ShieldCheck size={14} />
                              Active
                            </>
                          ) : (
                            <>
                              <ShieldAlert size={14} />
                              Inactive
                            </>
                          )}
                        </button>
                      </td>
                      <td style={{ ...tableCellStyle, textAlign: "right" }}>
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleToggleStatus(doctor)}
                            className="p-2.5 rounded-xl transition-all hover:scale-110 active:scale-95 border"
                            style={{
                              backgroundColor: doctor.IS_ACTIVE ? theme.primary + '15' : 'rgba(0,0,0,0.05)',
                              color: doctor.IS_ACTIVE ? theme.primary : '#94a3b8',
                              borderColor: doctor.IS_ACTIVE ? theme.primary + '30' : 'rgba(0,0,0,0.1)'
                            }}
                            title={doctor.IS_ACTIVE ? "Deactivate Account" : "Activate Account"}
                          >
                            <Power size={18} />
                          </button>
                          <button
                            onClick={() => navigate(`/edit-doctor/${doctor.MANDALI_ID}`)}
                            className="px-6 py-2.5 rounded-full text-sm font-bold border transition-all duration-200 hover:scale-105 active:scale-95 flex items-center justify-center gap-2 shadow-sm"
                            style={{
                              backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                              borderColor: theme.border,
                              color: theme.primary
                            }}
                          >
                            Details
                            <ChevronRight size={14} />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))
                ) : (
                  <tr className="border-0" style={{ backgroundColor: 'transparent' }}>
                    <td colSpan={5} className="py-24 text-center">
                      <UserSquare2 className="mx-auto mb-4 opacity-20" size={64} style={{ color: theme.text }} />
                      <p className="text-xl font-bold opacity-30" style={{ color: theme.text }}>No medical staff found matching your search</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Simple Watermark consistent with other refined pages */}
        <div className="mt-12 text-center text-xs opacity-30 font-medium tracking-widest uppercase" style={{ color: theme.text }}>
          Internal Administration Database • Amrutdhara
        </div>
      </div>
    </div>
  );
};

const ActionButton = ({ onClick, icon: Icon, label, theme, primary, isDarkMode }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-2 px-5 py-3 rounded-xl font-bold transition-all hover:scale-105 active:scale-95 shadow-lg border"
    style={{
      backgroundColor: primary ? theme.secondary : (isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.2)'),
      color: '#ffffff',
      borderColor: primary ? 'transparent' : 'rgba(255,255,255,0.3)',
      backdropFilter: 'blur(10px)'
    }}
  >
    <Icon size={18} />
    {label}
  </button>
);

export default ListofDoctors;
